import { createSlice } from "@reduxjs/toolkit";

import { getValidLandingScreen, setLocalStorage } from "../helpers/utils";
import {
  GET_USER_DETAILS,
  POST_INITIATE_RESET_PASSWORD,
  POST_LOGIN,
  POST_REFRESH_TOKEN,
  POST_RESET_PASSWORD,
} from "../helpers/url_helpers";
import { get, post } from "../helpers/axios";
import { LOGIN_CONNECTIVITY_ERROR_MESSAGE, SURVEYS } from "../helpers/constants";

const initialState = {
  loading: false,
  loginAccessToken: "",
  loginRefreshToken: "",
  loginError: "",
  userData: {},
  activeSurvey: SURVEYS.CSF,
};

const signIn = createSlice({
  name: "signIn",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload.loading;
    },
    setLogInToken: (state, action) => {
      state.loginAccessToken = action.payload.accessToken;
      state.loginRefreshToken = action.payload.refreshToken;
    },
    setUserDetails: (state, action) => {
      state.userData = action.payload.userData;
    },
    setError: (state, action) => {
      state.loginError = action.payload.loginError;
    },
    setActiveSurvey: (state, action) => {
      state.activeSurvey = action.payload;
    },
    logout: (state) => {},
  },
});

export const {
  setLoading,
  setLogInToken,
  setUserDetails,
  setError,
  logout,
  setActiveSurvey,
} = signIn.actions;
export default signIn.reducer;

export const login = (payload, history, callback) => async (dispatch) => {
  let response;
  try {
    dispatch(setLoading({ loading: true }));
    dispatch(setLogInToken({ loginAccessToken: "", loginRefreshToken: "" }));
    dispatch(setError({ loginError: "" }));

    response = await post(POST_LOGIN, payload);
    if (response) {
      if (response.error) {
        dispatch(setError({ loginError: response.error }));
      } else {
        if (response?.access && response?.refresh) {
          dispatch(
            setLogInToken({
              accessToken: response.access,
              refreshToken: response.refresh,
            })
          );
          setLocalStorage("AUTH_DETAILS", response);
        }
      }
      if (callback) callback(response?.error, response?.status);
    }
    dispatch(setLoading({ loading: false }));
  } catch (error) {
    if (error) {
      // On the login screen, a backend-unreachable error shows an
      // industry-standard message with a support contact line. Specific
      // server-provided errors (e.g. invalid credentials) pass through as-is.
      const loginError = error?.isNetworkError
        ? { detail: [LOGIN_CONNECTIVITY_ERROR_MESSAGE] }
        : error;
      dispatch(setError({ loginError }));
    }
    dispatch(setLoading({ loading: false }));
  }
};

export const getUserDetails = (history) => async (dispatch) => {
  let response;
  try {
    dispatch(setLoading({ loading: true }));
    dispatch(setError({ loginError: "" }));

    response = await get(GET_USER_DETAILS);
    if (response) {
      if (response.error) {
        dispatch(
          setError({
            loginError: response?.error,
          })
        );
      } else {
        dispatch(
          setUserDetails({
            userData: response?.data,
          })
        );
        setLocalStorage("USER_DETAILS", response?.data);
        history.push(getValidLandingScreen(response?.data));
      }
      dispatch(setLoading({ loading: false }));
    }
  } catch (error) {
    if (error) dispatch(setError({ loginError: error }));
    dispatch(setLoading({ loading: false }));
  }
};

export const intiateResetPassword =
  (data, history, callback) => async (dispatch) => {
    let response;
    try {
      dispatch(setLoading({ loading: true }));
      dispatch(setError({ loginError: "" }));

      response = await post(POST_INITIATE_RESET_PASSWORD, data);
      if (response) {
        if (response.error)
          dispatch(
            setError({
              loginError: response?.error,
            })
          );
        else {
          if (callback) callback();
          history.push("/sign-in");
        }
      }
      dispatch(setLoading({ loading: false }));
    } catch (error) {
      if (error) dispatch(setError({ loginError: error }));
      dispatch(setLoading({ loading: false }));
    }
  };

export const passwordReset = (data, history, callback) => async (dispatch) => {
  let response;
  try {
    dispatch(setLoading({ loading: true }));
    dispatch(setError({ loginError: "" }));

    response = await post(POST_RESET_PASSWORD, data);
    if (response) {
      if (response.error) dispatch(setError({ loginError: response.error }));
      else {
        if (callback) callback();
        history.push("/sign-in");
      }
    }
    dispatch(setLoading({ loading: false }));
  } catch (error) {
    if (error) dispatch(setError({ loginError: error }));
    dispatch(setLoading({ loading: false }));
  }
};

export const refreshTokenData = (data) => async (dispatch) => {
  let response;
  try {
    dispatch(setLoading({ loading: true }));
    dispatch(setError({ loginError: "" }));

    response = await post(POST_REFRESH_TOKEN, data);
    if (response) {
      if (response.error) dispatch(setError({ loginError: response.error }));
      else {
        const storeData = {
          access: response.access,
          refresh: data.refresh,
        };
        localStorage.removeItem("AUTH_DETAILS");
        setLocalStorage("AUTH_DETAILS", storeData);
      }
    }
    dispatch(setLoading({ loading: false }));
  } catch (error) {
    if (error) dispatch(setError({ loginError: error }));
    dispatch(setLoading({ loading: false }));
  }
};

export const clearError = () => async (dispatch) => {
  dispatch(setError({ loginError: "" }));
};
